import {
  DuplicateContactsSelectionCancelledError,
  useResolveDuplicatesBeforeAdding,
} from '@components/duplicate-contacts-modal/useResolveDuplicatesBeforeAdding';
import { useNotifications } from '@components/Notifications.tsx';
import { getExistingContacts } from '@contact/services/contactsService';
import { useMediaDatabaseLocalStorageSearchQuery } from '@media-database/hooks/mediaDatabaseLocalStorageSearchQuery';
import { initialNmdbFiltersState } from '@media-database/hooks/mediaDatabaseSearch';
import {
  addToLists,
  categorizeIptc,
  getJournalist,
  getJournalistArticles,
  getJournalistEmail,
  getOutlet,
  getOutletEmail,
  getOutlets,
  getOutletsJournalists,
  getPitchRecommendation,
  getProfileBio,
  getRelatedJournalists,
  nmdbSearch,
} from '@media-database/services/mediaDatabaseService';
import {
  AddToListPayload,
  MediaDatabaseJournalist,
  MediaDatabaseOutlet,
  NmdbFiltersValue,
  NmdbSearchParams,
  NmdbSearchResponse,
  OutletsJournalistsFilters,
  OutletsJournalistsResponse,
  OutletsResponse,
  Paginator,
  PitchRecommendationPayload,
  PitchRecommendationResponse,
  ProfileBio,
  RelatedJournalistsResponse,
  SelectedContact,
} from '@media-database/types/mediaDatabase';
import { MEDIA_LIST_BASE_KEY } from '@media-lists/hooks/mediaListsQueries.ts';
import Button from '@semcore/ui/button';
import { invalidateSubscriptionQuery } from '@src/hooks/queries/accountQueries';
import { fetchDiscoveryToken, getDiscoveryToken } from '@src/services/discoveryTokenService';
import { ContactType } from '@src/types/contactType';
import {
  ContactBeingAdded,
  ContactDuplicateId,
  DiscoveryContactDetails,
} from '@src/types/duplicateContacts';
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import camelCase from 'camelcase';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const OUTLET_JOURNALISTS_PAGE_SIZE = 50;
const OUTLET_JOURNALISTS_FACETS = ['topics'];

export interface OutletJournalistsFilters {
  name?: string;
  topics?: string[];
}

const OUTLETS_JOURNALISTS_REFETCH_INTERVAL = 1000;

export const MEDIA_DATABASE_BASE_KEY = 'media-database';
export const MEDIA_DATABASE_SEARCH_KEY = [MEDIA_DATABASE_BASE_KEY, 'search'];

export const mediaDatabaseQueries = {
  nmdbSearch: (searchParams: NmdbSearchParams, filters: NmdbFiltersValue) =>
    queryOptions<NmdbSearchResponse | null>({
      queryKey: [...MEDIA_DATABASE_SEARCH_KEY, 'nmdb', searchParams, filters] as const,
      queryFn: () => nmdbSearch(searchParams, filters),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }),
  nmdbSearchList: (
    searchParams: NmdbSearchParams,
    filters: NmdbFiltersValue,
    isQueryModeEnabled: boolean,
  ) =>
    queryOptions<NmdbSearchResponse | null>({
      queryKey: [...MEDIA_DATABASE_SEARCH_KEY, 'nmdb', isQueryModeEnabled] as const,
      queryFn: () => nmdbSearch(searchParams, filters, isQueryModeEnabled),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: Infinity,
      placeholderData: keepPreviousData,
    }),
  journalist: (id: string) =>
    queryOptions<MediaDatabaseJournalist, AxiosError>({
      queryKey: [MEDIA_DATABASE_BASE_KEY, 'journalist', id],
      queryFn: () => getJournalist(id),
    }),
  journalistEmail: (id: string) =>
    queryOptions<{ email: string }, AxiosError>({
      queryKey: [MEDIA_DATABASE_BASE_KEY, 'journalistEmail', id],
      queryFn: () => getJournalistEmail(id),
      enabled: false,
    }),
  outlet: (id: string) =>
    queryOptions<MediaDatabaseOutlet, AxiosError>({
      queryKey: [MEDIA_DATABASE_BASE_KEY, 'outlet', id],
      queryFn: () => getOutlet(id),
    }),
  outletEmail: (id: string) =>
    queryOptions<{ email: string }, AxiosError>({
      queryKey: [MEDIA_DATABASE_BASE_KEY, 'outletEmail', id],
      queryFn: () => getOutletEmail(id),
      enabled: false,
    }),
  outlets: (ids: string[]) =>
    queryOptions<OutletsResponse>({
      queryKey: [MEDIA_DATABASE_BASE_KEY, 'outlets', ids],
      queryFn: () => getOutlets(ids),
    }),
  relatedJournalists: (id: string) =>
    queryOptions<RelatedJournalistsResponse>({
      queryKey: [MEDIA_DATABASE_BASE_KEY, 'relatedJournalists', id],
      queryFn: () => getRelatedJournalists(id),
    }),
  journalistArticles: (journalistId: string) =>
    infiniteQueryOptions({
      queryKey: [MEDIA_DATABASE_BASE_KEY, 'journalist', journalistId, 'articles'],
      queryFn: ({ pageParam }) => getJournalistArticles(journalistId, pageParam),
      initialPageParam: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      getNextPageParam: ({ paginator }: { paginator?: Paginator }) => {
        if (!paginator) {
          return undefined;
        }

        if (!paginator.currentPage || !paginator.pageSize || !paginator.totalCount) {
          return undefined;
        }
        return paginator.currentPage * paginator.pageSize < paginator.totalCount
          ? paginator.currentPage + 1
          : undefined;
      },
    }),
  outletJournalists: (id: string, { name = '', topics = [] }: OutletJournalistsFilters = {}) =>
    infiniteQueryOptions({
      queryKey: [MEDIA_DATABASE_BASE_KEY, 'outlet', id, 'journalists', { name, topics }],
      queryFn: ({ pageParam }: { pageParam: number }) =>
        nmdbSearch(
          {
            page: pageParam,
            pageSize: OUTLET_JOURNALISTS_PAGE_SIZE,
            contactTypeEquals: 'person',
            outletId: id,
            facets: OUTLET_JOURNALISTS_FACETS,
            name,
          },
          { ...initialNmdbFiltersState, topics },
        ),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      initialPageParam: 1,
      placeholderData: keepPreviousData,
      getNextPageParam: (data) => {
        if (!data?.paginator) return undefined;

        const { currentPage, pageSize, totalCount, lastPage } = data.paginator;
        const hasNextPage =
          lastPage == null ? currentPage * pageSize < totalCount : currentPage < lastPage;

        return hasNextPage ? currentPage + 1 : undefined;
      },
    }),
  outletsJournalists: (outletIds: string[], filters?: OutletsJournalistsFilters) =>
    queryOptions<OutletsJournalistsResponse>({
      queryKey: ['outletsJournalists', outletIds, filters],
      queryFn: () => getOutletsJournalists(outletIds, filters),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }),
  pitchRecommendation: (payload: PitchRecommendationPayload) =>
    queryOptions<PitchRecommendationResponse>({
      queryKey: ['pitchRecommendation', payload.query],
      queryFn: () => getPitchRecommendation(payload),
      refetchOnWindowFocus: false,
    }),
  profileBio: (journalistId: string) =>
    queryOptions<ProfileBio>({
      queryKey: [MEDIA_DATABASE_BASE_KEY, 'journalist', journalistId, 'profileBio'],
      queryFn: () => getProfileBio(journalistId),
    }),
};

export const useNmdbSearch = (
  searchParams: NmdbSearchParams,
  filters: NmdbFiltersValue,
  isEnabled = true,
) => {
  return useQuery<NmdbSearchResponse | null>({
    ...mediaDatabaseQueries.nmdbSearch(searchParams, filters),
    enabled: isEnabled,
  });
};

export const useContactSearch = (
  searchParams: NmdbSearchParams,
  filters: NmdbFiltersValue,
  isEnabled = false,
  isQueryModeEnabled = false,
) => {
  const { setStoredNmdbSearchQuery } = useMediaDatabaseLocalStorageSearchQuery();
  useEffect(() => {
    setStoredNmdbSearchQuery(filters, searchParams);
  }, [filters, searchParams, setStoredNmdbSearchQuery]);

  const defaultSearchQuery = useQuery<NmdbSearchResponse | null>({
    ...mediaDatabaseQueries.nmdbSearchList(searchParams, filters, false),
    enabled: isEnabled && !isQueryModeEnabled,
  });
  const naturalSearchQuery = useQuery<NmdbSearchResponse | null>({
    ...mediaDatabaseQueries.nmdbSearchList(searchParams, filters, true),
    enabled: isEnabled && isQueryModeEnabled,
  });

  const activeSearchQuery = isQueryModeEnabled ? naturalSearchQuery : defaultSearchQuery;

  return {
    data: activeSearchQuery.data,
    refetch: activeSearchQuery.refetch,
    isFetching: activeSearchQuery.isFetching,
    isPending: activeSearchQuery.isPending,
    defaultSearchQuery,
    naturalSearchQuery,
  };
};

export const useOutletJournalists = (
  id: string,
  isEnabled = true,
  filters?: OutletJournalistsFilters,
) => {
  return useInfiniteQuery({
    ...mediaDatabaseQueries.outletJournalists(id, filters),
    enabled: isEnabled,
  });
};

const getDuplicateCheckIdsBySourceUid = async (selectedContacts: SelectedContact[]) => {
  const existingContacts = await getExistingContacts({
    sourceUids: selectedContacts.map(({ id }) => id),
  });

  return new Map<string, ContactDuplicateId>(
    selectedContacts.map(({ id }) => [id, existingContacts[camelCase(id)]?.id ?? id]),
  );
};

const getContactsBeingAdded = (
  selectedContacts: SelectedContact[],
  duplicateCheckIdsBySourceUid: Map<string, ContactDuplicateId>,
): ContactBeingAdded[] =>
  selectedContacts.flatMap(({ id, name, email }) => {
    const duplicateCheckId = duplicateCheckIdsBySourceUid.get(id);

    return duplicateCheckId === undefined
      ? []
      : [{ id: duplicateCheckId, name: name ?? email ?? '', email, vendorId: id }];
  });

const getDiscoveryContactDetails = async (
  contacts: SelectedContact[],
): Promise<Record<string, DiscoveryContactDetails>> => {
  if (contacts.length === 0) {
    return {};
  }

  const token = getDiscoveryToken() ?? (await fetchDiscoveryToken());

  return Object.fromEntries(
    contacts.map(({ id, contactType }) => [
      id,
      { contactType: contactType === 'outlet' ? 'outlet' : 'journalist', token } as const,
    ]),
  );
};

export const useAddToLists = (
  onSuccess?: (mediaListsId: number[]) => void | Promise<void>,
  onBeforeRefetch?: () => void,
) => {
  const { t } = useTranslation(['mediaDatabase']);
  const { showSuccessNotification, showErrorNotification, removeNotification } = useNotifications();
  const resolveDuplicatesBeforeAdding = useResolveDuplicatesBeforeAdding();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const completeAddToListsSuccess = async (
    { selectedContacts, mediaListsId }: AddToListPayload,
    addedContactsSize?: number,
  ) => {
    onBeforeRefetch?.();
    await queryClient.invalidateQueries({ queryKey: [MEDIA_DATABASE_BASE_KEY] });

    void onSuccess?.(mediaListsId);

    const { uid } = showSuccessNotification(
      t('mediaDatabase:contactAddedSuccessfully', {
        count: addedContactsSize ?? selectedContacts.length,
      }),
      <Button
        data-test="add-to-lists-success-link"
        tabIndex={-1}
        theme="invert"
        onClick={() => {
          void navigate({ to: '/pr-toolkit/media-lists' });
          removeNotification(uid);
        }}>
        {t('mediaDatabase:goToList_other')}
      </Button>,
    );

    void queryClient.invalidateQueries({
      queryKey: [MEDIA_LIST_BASE_KEY] as const,
    });
    invalidateSubscriptionQuery();
  };

  return useMutation({
    mutationFn: async (addToListPayload: AddToListPayload) => {
      const duplicateCheckIdsBySourceUid = await getDuplicateCheckIdsBySourceUid(
        addToListPayload.selectedContacts,
      );
      const discoveryContacts = await getDiscoveryContactDetails(addToListPayload.selectedContacts);
      const decision = await resolveDuplicatesBeforeAdding({
        contactsBeingAdded: getContactsBeingAdded(
          addToListPayload.selectedContacts,
          duplicateCheckIdsBySourceUid,
        ),
        discoveryContacts,
        mediaListsId: addToListPayload.mediaListsId,
      });

      if (decision.isCancelled) {
        throw new DuplicateContactsSelectionCancelledError();
      }

      const selectedContacts = addToListPayload.selectedContacts.filter(({ id }) => {
        const duplicateCheckId = duplicateCheckIdsBySourceUid.get(id);

        return duplicateCheckId !== undefined && !decision.contactIdsToSkip.has(duplicateCheckId);
      });

      if (selectedContacts.length === 0) {
        return { addedContactsSize: 0 };
      }

      return addToLists({ ...addToListPayload, selectedContacts });
    },
    onError: (error, { selectedContacts }) => {
      if (error instanceof DuplicateContactsSelectionCancelledError) {
        return;
      }

      showErrorNotification(
        t('mediaDatabase:failedToAddToList', { count: selectedContacts.length }),
      );
    },
    onSuccess: (response, variables) => {
      return completeAddToListsSuccess(variables, response.addedContactsSize);
    },
  });
};

export const useMediaDatabaseJournalistQuery = (
  id: string,
  options?: Omit<UseQueryOptions<MediaDatabaseJournalist, AxiosError>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    ...mediaDatabaseQueries.journalist(id),
    ...options,
  });
};

export const useMediaDatabaseJournalistEmailQuery = (id: string) => {
  return useQuery(mediaDatabaseQueries.journalistEmail(id));
};

export const useMediaDatabaseOutletEmailQuery = (id: string) => {
  return useQuery(mediaDatabaseQueries.outletEmail(id));
};

export const useMediaDatabaseOutletQuery = (
  id: string,
  options?: Omit<UseQueryOptions<MediaDatabaseOutlet, AxiosError>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    ...mediaDatabaseQueries.outlet(id),
    ...options,
  });
};

export const useMediaDatabaseContactQuery = (contactType: ContactType, id: string) => {
  const journalistQuery = useMediaDatabaseJournalistQuery(id, {
    enabled: contactType === 'journalist',
  });
  const outletQuery = useMediaDatabaseOutletQuery(id, { enabled: contactType === 'outlet' });

  return contactType === 'journalist' ? journalistQuery : outletQuery;
};

export const useMediaDatabaseContactEmailQuery = (contactType: ContactType, id: string) => {
  const journalistEmailQuery = useMediaDatabaseJournalistEmailQuery(id);
  const outletEmailQuery = useMediaDatabaseOutletEmailQuery(id);

  return contactType === 'journalist' ? journalistEmailQuery : outletEmailQuery;
};

export const useJournalistArticlesQuery = (
  journalistId: string,
  options?: Partial<ReturnType<typeof mediaDatabaseQueries.journalistArticles>>,
) => {
  return useInfiniteQuery({
    ...mediaDatabaseQueries.journalistArticles(journalistId),
    ...options,
  });
};

export const useRelatedJournalistsQuery = (
  journalistId: string,
  options?: Omit<UseQueryOptions<RelatedJournalistsResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    ...mediaDatabaseQueries.relatedJournalists(journalistId),
    ...options,
  });
};

export const useOutletsQuery = (
  outletIds: string[],
  options?: Omit<UseQueryOptions<OutletsResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    ...mediaDatabaseQueries.outlets(outletIds),
    ...options,
  });
};

export const useOutletsJournalists = (
  outletIds: string[],
  filters?: OutletsJournalistsFilters,
  options?: Omit<
    UseQueryOptions<OutletsJournalistsResponse>,
    'queryKey' | 'queryFn' | 'refetchInterval'
  >,
) => {
  return useQuery({
    ...mediaDatabaseQueries.outletsJournalists(outletIds, filters),
    enabled: options?.enabled ?? true,
    refetchInterval: (query) => {
      if (query.state.error) {
        return false;
      }

      if (!query.state.data?.status || query.state.data.status === 'PROCESSING') {
        return OUTLETS_JOURNALISTS_REFETCH_INTERVAL;
      }

      return false;
    },
    ...options,
  });
};

export const usePitchRecommendation = (
  payload: PitchRecommendationPayload,
  options?: Omit<UseQueryOptions<PitchRecommendationResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    ...mediaDatabaseQueries.pitchRecommendation(payload),
    ...options,
  });
};

export const useProfileBioQuery = (
  journalistId: string,
  options?: Omit<UseQueryOptions<ProfileBio>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    ...mediaDatabaseQueries.profileBio(journalistId),
    ...options,
  });
};

export const useCategorizeIptcMutation = () => {
  return useMutation({
    mutationFn: (content: string) => categorizeIptc(content),
  });
};
